/* eslint-disable */
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PowerMetricsColor } from '../../../components/globalStyles';
import ChargingProgress from '../../../components/chargingProgress/ChargingProgress';
import { LoadingSm } from '../../../components/loaders/localLoading/LocalLoading';
import ModalContacts from '../../contactsPage/modal/ModalContacts';
import { api } from '../../../redux/api';
import ReactGA from 'react-ga';
import { Button, ActionButtonSpinner, getActionButtonLoadingClassName } from '@ui';
import {
  startChargingLiveActivity,
  updateChargingLiveActivity,
  endChargingLiveActivity,
} from '../../../hookServices/iosChargingLiveActivity';

import { useGetClientId } from '../../../hookServices/clientId';
import { isEnergyCardChargingSession } from '../../../hookServices/energyCard';
import { useContactUsTelegramPreferred } from '../../../hookServices/useContactUsTelegramPreferred';
import { TELEGRAM_BOT } from '../../../environment';
import { Card } from '../../../shared/ui';
import { Box } from '@mui/material';
import {
  getDynamicTariffScheduleFromStation,
  getPortInServiceMessage,
  isDcPort,
  isStationInService,
} from '../../../utils/utils';
import { useGetActiveEnergyCardsQuery } from '../../../redux/api';
import { getSocColors } from '../../../utils/socColor';
import './onlineStation.css';
import { scrollGreenEnergyCarouselToCenter as scrollGreenEnergyCarouselToCenterRoot } from '../../../utils/greenEnergyEcoCard';
import {
  GREEN_ENERGY_FLOW_BUBBLE_COUNT,
  greenEnergyFlowBubbleStyle,
  greenEnergyFlowsNearlyEqual,
  measureGreenEnergyIconFlowPx,
} from '../../../utils/greenEnergyFlow';
import '../../../components/greenEnergyFlow.css';
import '../../../components/greenEnergyMainCard.css';
import { Chart } from '../fullInfo/Chart';
import ChargingPowerHistoryModal from './ChargingPowerHistoryModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StopChargingConfirmModal from '../../../components/StopChargingConfirmModal';

import {
  OPEN_EMS_BASE,
  computeEmsRenewablePct,
  fetchAggregatedOpenEmsTelemetry,
  resolveEvPortInverterBindings,
  resolveOpenEmsInverterUrl,
} from '../../../utils/openEmsEvPortBattery';
import { getSocPriceTiersFromStation } from '../../../utils/socDynamicPriceNotes';
import EmsBatteryEcoCard from '../../../components/EmsBatteryEcoCard/EmsBatteryEcoCard';
import EmsSolarEcoCard from '../../../components/EmsSolarEcoCard/EmsSolarEcoCard';
/** Alumni Sans can draw '%' as wrong glyph; force a stack with a reliable U+0025. */
const FONT_STACK_PERCENT_SAFE =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif';

export default function OnlineStation({
  station,
  stationStatus,
  periodKwh,
  kWhCharged,
  costPerKwt,
  kWtPower,
  chargeStatus,
  onlineStatus,
  timer,
  isZero,
  lastJob,
  carKwtKmRatio,
  hasActualKmRatio = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const urlProtocol = window.location.protocol;
  const urlHost = window.location.host;
  const [hideSpinner, setHideSpinner] = useState(true);
  const [stopConfirmOpen, setStopConfirmOpen] = useState(false);
  const [powerHistoryOpen, setPowerHistoryOpen] = useState(false);
  const [addTooltipOpen, setAddTooltipOpen] = useState(false);
  const [tariffTooltipOpen, setTariffTooltipOpen] = useState(false);
  const addTooltipRef = useRef(null);
  const isDcChargingPort = isDcPort(station?.plugType, station?.maxPowerWt);
  const socPriceTiers = useMemo(() => getSocPriceTiersFromStation(station), [station]);
  const { clientId } = useGetClientId();
  const { data: activeEnergyCards } = useGetActiveEnergyCardsQuery(clientId, {
    skip: !clientId,
    pollingInterval: 7000,
  });
  const liveEnergyCards = useMemo(() => (activeEnergyCards || []).filter(c => !c.expired), [activeEnergyCards]);
  const isEcSession = isEnergyCardChargingSession({
    invoiceId: lastJob?.invoiceId,
    showStop: lastJob?.showStop,
    stationNumber: station?.number,
    energyCards: liveEnergyCards,
  });
  // Session allocation from start-page slider (periodKwt), not full Energy Card balance.
  const sessionPeriodKwh = useMemo(() => {
    const period = Number(periodKwh ?? lastJob?.periodKwt);
    if (!Number.isFinite(period) || period <= 0 || period === 220) {
      return null;
    }
    return period;
  }, [periodKwh, lastJob?.periodKwt]);
  const sessionLeftKwh = useMemo(() => {
    if (sessionPeriodKwh == null) {
      return null;
    }
    const charged = Math.max(0, Number(kWhCharged) || 0);
    return Math.max(0, sessionPeriodKwh - charged);
  }, [sessionPeriodKwh, kWhCharged]);
  const kwhChargeProgress = useMemo(() => {
    if (sessionPeriodKwh == null) {
      return null;
    }
    const charged = Math.max(0, Number(kWhCharged) || 0);
    return {
      pct: Math.min(100, Math.max(0, (charged / sessionPeriodKwh) * 100)),
    };
  }, [sessionPeriodKwh, kWhCharged]);
  const openPowerHistoryChart = useCallback(() => {
    setPowerHistoryOpen(true);
  }, []);

  const [emsBound, setEmsBound] = useState(false);
  const emsBindingsRef = useRef([]);
  const [openEmsInverterBindings, setOpenEmsInverterBindings] = useState([]);
  const [emsBatteryCapacityKwh, setEmsBatteryCapacityKwh] = useState(null);
  const [emsData, setEmsData] = useState({
    pvPowerW: null,
    batteryPowerW: null,
    batteryDischargePowerW: null,
    gridPowerW: null,
    loadPowerW: null,
  });
  const [emsSocPercent, setEmsSocPercent] = useState(null);
  const [emsInsolation, setEmsInsolation] = useState({
    todayPct: null,
    tomorrowPct: null,
  });
  const openEmsInverterUrl = useMemo(
    () => resolveOpenEmsInverterUrl(openEmsInverterBindings, emsBatteryCapacityKwh),
    [openEmsInverterBindings, emsBatteryCapacityKwh]
  );

  const greenEnergyCarouselRef = useRef(null);
  const greenEnergySolarIconRef = useRef(null);
  const greenEnergyBatteryIconRef = useRef(null);
  const greenEnergyTreeBadgeRef = useRef(null);

  const emsPvKw = emsData.pvPowerW != null ? Math.max(0, Number(emsData.pvPowerW)) / 1000 : null;
  const emsBatDischargeKw =
    emsData.batteryDischargePowerW != null && Number(emsData.batteryDischargePowerW) > 0
      ? Number(emsData.batteryDischargePowerW) / 1000
      : emsData.batteryPowerW != null && Number(emsData.batteryPowerW) > 0
        ? Number(emsData.batteryPowerW) / 1000
        : null;
  const emsChargeKwBlend = onlineStatus === false ? 0 : Math.max(kWtPower, 0.01);
  const emsRenewablePct =
    emsBound && onlineStatus !== false
      ? computeEmsRenewablePct({
          chargeKw: emsChargeKwBlend,
          pvPowerW: emsData.pvPowerW,
          batteryPowerW: emsData.batteryPowerW,
          batteryDischargePowerW: emsData.batteryDischargePowerW,
          gridPowerW: emsData.gridPowerW,
          loadPowerW: emsData.loadPowerW,
          online: onlineStatus !== false,
        })
      : null;
  const showSolarBubblesFlow = emsRenewablePct != null && emsPvKw != null && emsPvKw > 0.02;
  const showBatteryBubblesFlow = emsRenewablePct != null && emsBatDischargeKw != null && emsBatDischargeKw > 0.02;

  const [greenEnergySolarFlowPx, setGreenEnergySolarFlowPx] = useState(null);
  const [greenEnergyBatteryFlowPx, setGreenEnergyBatteryFlowPx] = useState(null);

  const fetchEmsTelemetry = useCallback(async bindings => {
    if (!bindings?.length) return;
    try {
      const aggregated = await fetchAggregatedOpenEmsTelemetry(bindings);
      if (aggregated.batteryCapacityKwh != null) {
        setEmsBatteryCapacityKwh(aggregated.batteryCapacityKwh);
      }
      if (
        aggregated.pvPowerW != null ||
        aggregated.batteryPowerW != null ||
        aggregated.gridPowerW != null ||
        aggregated.loadPowerW != null
      ) {
        setEmsData({
          pvPowerW: aggregated.pvPowerW ?? null,
          batteryPowerW: aggregated.batteryPowerW ?? null,
          batteryDischargePowerW: aggregated.batteryDischargePowerW ?? null,
          gridPowerW: aggregated.gridPowerW ?? null,
          loadPowerW: aggregated.loadPowerW ?? null,
        });
      }
      if (aggregated.socPercent != null) {
        setEmsSocPercent(aggregated.socPercent);
      }
      if (aggregated.insolationTodayPct != null || aggregated.insolationTomorrowPct != null) {
        setEmsInsolation({
          todayPct: aggregated.insolationTodayPct ?? null,
          tomorrowPct: aggregated.insolationTomorrowPct ?? null,
        });
      }
    } catch {
      /* keep stale */
    }
  }, []);

  const measureGreenEnergyIconFlows = useCallback(() => {
    const treeEl = greenEnergyTreeBadgeRef.current;
    if (!treeEl) {
      setGreenEnergySolarFlowPx(null);
      setGreenEnergyBatteryFlowPx(null);
      return;
    }

    if (showSolarBubblesFlow && greenEnergySolarIconRef.current) {
      const next = measureGreenEnergyIconFlowPx(greenEnergySolarIconRef.current, treeEl);
      setGreenEnergySolarFlowPx(prev => (greenEnergyFlowsNearlyEqual(prev, next) ? prev : next));
    } else {
      setGreenEnergySolarFlowPx(null);
    }

    if (showBatteryBubblesFlow && greenEnergyBatteryIconRef.current) {
      const next = measureGreenEnergyIconFlowPx(greenEnergyBatteryIconRef.current, treeEl, { fromEdge: 'top' });
      setGreenEnergyBatteryFlowPx(prev => (greenEnergyFlowsNearlyEqual(prev, next) ? prev : next));
    } else {
      setGreenEnergyBatteryFlowPx(null);
    }
  }, [showSolarBubblesFlow, showBatteryBubblesFlow]);

  const scrollGreenEnergyCarouselToCenter = useCallback(() => {
    scrollGreenEnergyCarouselToCenterRoot(greenEnergyCarouselRef.current);
    queueMicrotask(() => measureGreenEnergyIconFlows());
  }, [measureGreenEnergyIconFlows]);

  useEffect(() => {
    if (!station?.number || !station?.name) return;
    let cancelled = false;

    const lookupInverter = async () => {
      try {
        const r = await fetch(`${OPEN_EMS_BASE}/api/deye/inverters`, { cache: 'no-store' });
        const data = await r.json().catch(() => ({}));
        if (!r.ok || !data.configured) return;
        const bindings = resolveEvPortInverterBindings(data.items, station.name);
        if (bindings.length === 0 || cancelled) return;
        emsBindingsRef.current = bindings;
        setOpenEmsInverterBindings(bindings);
        setEmsBound(true);
        await fetchEmsTelemetry(bindings);
      } catch {
        /* ignore */
      }
    };

    void lookupInverter();
    return () => {
      cancelled = true;
    };
  }, [station?.number, station?.name, fetchEmsTelemetry]);

  useEffect(() => {
    if (!emsBound) return;
    const id = setInterval(() => void fetchEmsTelemetry(emsBindingsRef.current), 7000);
    return () => clearInterval(id);
  }, [emsBound, fetchEmsTelemetry]);

  useLayoutEffect(() => {
    if (!emsBound) return;
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => scrollGreenEnergyCarouselToCenter());
    });
    return () => window.cancelAnimationFrame(id);
  }, [emsBound, scrollGreenEnergyCarouselToCenter]);

  useEffect(() => {
    const root = greenEnergyCarouselRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => scrollGreenEnergyCarouselToCenter());
    ro.observe(root);
    window.addEventListener('orientationchange', scrollGreenEnergyCarouselToCenter);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', scrollGreenEnergyCarouselToCenter);
    };
  }, [emsBound, scrollGreenEnergyCarouselToCenter]);

  useLayoutEffect(() => {
    if (!emsBound) {
      setGreenEnergySolarFlowPx(null);
      setGreenEnergyBatteryFlowPx(null);
      return;
    }
    let raf = 0;
    const run = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => measureGreenEnergyIconFlows());
    };
    run();
    window.addEventListener('resize', run);
    window.addEventListener('scroll', run, true);
    const car = greenEnergyCarouselRef.current;
    car?.addEventListener('scroll', run);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', run);
      window.removeEventListener('scroll', run, true);
      car?.removeEventListener('scroll', run);
    };
  }, [
    emsBound,
    measureGreenEnergyIconFlows,
    emsRenewablePct,
    kWtPower,
    onlineStatus,
    emsPvKw,
    emsBatDischargeKw,
    showSolarBubblesFlow,
    showBatteryBubblesFlow,
    lastJob?.powerWt,
  ]);

  const socForUi = isDcChargingPort ? (lastJob?.soc ?? null) : null;
  const socUiColors = useMemo(() => getSocColors(socForUi), [socForUi]);
  const liveActivityPortNumber =
    lastJob?.portNumber ?? lastJob?.port ?? lastJob?.devicePortNumber ?? lastJob?.number ?? station?.number ?? '';

  // Keep a ref to the latest charging values for use in the cleanup callback
  const latestLiveActivityData = useRef({ chargedKwh: kWhCharged, powerKw: kWtPower, soc: socForUi });
  useEffect(() => {
    latestLiveActivityData.current = { chargedKwh: kWhCharged, powerKw: kWtPower, soc: socForUi };
  }, [kWhCharged, kWtPower, socForUi]);

  const [stopCharging] = api.useLazyStopProcessChargingQuery();
  const { preferTelegram: preferTelegramForContact, lastPortForLink } = useContactUsTelegramPreferred();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Start Live Activity when component mounts (charging is IN_PROGRESS)
  useEffect(() => {
    const periodKwt = lastJob?.periodKwt ?? 0;
    startChargingLiveActivity({
      stationNumber: String(liveActivityPortNumber),
      periodKwh: periodKwt === 220 ? 0 : periodKwt,
      wattsPerKm: carKwtKmRatio ?? 200,
      chargedKwh: kWhCharged,
      powerKw: kWtPower,
      soc: socForUi,
      leftSeconds: lastJob?.leftS ?? 0,
      isOnline: Boolean(onlineStatus),
    });
    return () => {
      const d = latestLiveActivityData.current;
      endChargingLiveActivity({ chargedKwh: d.chargedKwh, powerKw: d.powerKw, soc: d.soc });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Push updates to Live Activity whenever charging data changes
  useEffect(() => {
    if (!lastJob) return;
    updateChargingLiveActivity({
      chargedKwh: kWhCharged,
      powerKw: kWtPower,
      soc: socForUi,
      leftSeconds: lastJob?.leftS ?? 0,
      isOnline: Boolean(onlineStatus),
    });
  }, [kWhCharged, kWtPower, socForUi, lastJob?.leftS, onlineStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-expand add tooltip after 5 seconds (only if addTitle exists)
  useEffect(() => {
    const hasAddTitle = station?.name?.includes('addTitle:');
    if (!hasAddTitle) return;

    const timer = setTimeout(() => {
      setAddTooltipOpen(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [station?.name]);

  // Close add tooltip on outside click
  useEffect(() => {
    function handleDocumentClick(event) {
      if (!addTooltipOpen) return;

      // Check if click is outside the add-tooltip element
      const tooltipElement = document.querySelector('.add-tooltip');
      if (tooltipElement && !tooltipElement.contains(event.target)) {
        // Also check if click is not on the info icon that triggers the tooltip
        if (addTooltipRef.current && !addTooltipRef.current.contains(event.target)) {
          setAddTooltipOpen(false);
        }
      }
    }

    document.addEventListener('click', handleDocumentClick, true);
    return () => document.removeEventListener('click', handleDocumentClick, true);
  }, [addTooltipOpen]);

  function callToSupport() {
    if (!onlineStatus) {
      if (preferTelegramForContact && clientId) {
        ReactGA.event({
          category: 'contacts',
          action: 'telegram from charging page offline (last session port < 30 kW)',
        });
        window.open(
          `https://t.me/${TELEGRAM_BOT}?start=${encodeURIComponent(clientId)}-${lastPortForLink || station?.number || ''}`,
          '_blank'
        );
      } else {
        setIsOpen(true);
      }
    }
  }

  async function stopProcessCharging() {
    setHideSpinner(false);

    const { isSuccess, error } = await stopCharging({ portNumber: station.number });
    if (isSuccess) {
      setStopConfirmOpen(false);
      setHideSpinner(true);
    } else if (error) {
      console.error(error);
      setHideSpinner(true);
      setStopConfirmOpen(false);
      alert(t('stopChargingConfirm.error') || 'Failed to stop charging. Please try again.');
    }
  }

  function showPowerInfo() {
    const hasAddTitle = station?.name?.includes('addTitle:');
    const addTitle = station?.name?.match(/addTitle:"([^"]+)"/i)?.[1];
    const addLink = station?.name?.match(/addLink:"([^"]+)"/i)?.[1];

    const pvKw = emsPvKw;
    const hasEms = emsBound;
    const renewablePct = emsRenewablePct;
    const isGreen = emsRenewablePct != null && emsRenewablePct >= 70;

    return (
      <>
        <Box className="charging-page-content-wrap" sx={{ m: { xs: '16px 12px', md: '24px 3%' } }}>
          {hasEms && (
            <div className="charging-page-ems-carousel-outer">
              <div ref={greenEnergyCarouselRef} className="green-energy-carousel charging-page-ems-carousel">
                {/* Solar card */}
                <EmsSolarEcoCard
                  insolationTodayPct={emsInsolation.todayPct}
                  insolationTomorrowPct={emsInsolation.tomorrowPct}
                  pvPowerW={emsData.pvPowerW}
                  latitude={station?.latitude}
                  longitude={station?.longitude}
                  iconRef={greenEnergySolarIconRef}
                />

                {/* Center power card */}
                <Card
                  className={['green-energy-main-card', isGreen ? 'green-energy-main-card--renewable' : '']
                    .filter(Boolean)
                    .join(' ')}
                  variant="invert"
                  style={{
                    flex: '1 1 0',
                    minWidth: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBlock: '0',
                    position: 'relative',
                    overflow: 'visible',
                    scrollSnapAlign: 'center',
                  }}
                >
                  <Col
                    id="onlineStationKmChargedBox"
                    xs={12}
                    className="text-center green-energy-main-card-content green-energy-main-card__power-block"
                  >
                    <PowerMetricsColor
                      id="onlineStationKmChargedTitle"
                      className="mb-0 green-energy-main-card__power-title"
                    >
                      {t('power')}
                    </PowerMetricsColor>
                    <p
                      id="onlineStationKmChargedInfo"
                      className={[
                        'green-energy-main-card__power-value',
                        onlineStatus === false ? 'charging-power-value--offline' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {onlineStatus === false ? (
                        <>{t('onlineStatus')}</>
                      ) : (
                        <>
                          {kWtPower.toFixed(2)}
                          {t('powerWt')}
                        </>
                      )}
                    </p>
                    {renewablePct != null && (
                      <div
                        ref={greenEnergyTreeBadgeRef}
                        className={['green-energy-tree-badge', isGreen ? 'green-energy-tree-badge--active' : '']
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <span className="green-energy-tree-badge__icon" aria-hidden>
                          🌿
                        </span>
                        <span className="green-energy-tree-badge__text">
                          <span
                            className="green-energy-tree-badge__pct"
                            style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}
                          >
                            {renewablePct}%
                          </span>
                          {` ${t('renewable')}`}
                        </span>
                      </div>
                    )}
                  </Col>

                  {hasAddTitle && addTitle && addLink && (
                    <div
                      ref={addTooltipRef}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        margin: '12px 0',
                      }}
                    >
                      <span style={{ position: 'relative', display: 'inline-block' }}>
                        <InfoOutlinedIcon
                          style={{
                            color: '#C83DC5',
                            cursor: 'pointer',
                            fontSize: 26,
                            display: 'block',
                            margin: '0 auto',
                          }}
                          onMouseOver={() => setAddTooltipOpen(true)}
                          onMouseOut={() => setAddTooltipOpen(false)}
                        />
                        {addTooltipOpen && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: '100%',
                              transform: 'translateX(-50%)',
                              marginTop: '8px',
                              background: '#111',
                              color: '#fff',
                              border: '1px solid #C83DC5',
                              borderRadius: '8px',
                              padding: '14px 20px',
                              whiteSpace: 'normal',
                              zIndex: 1000,
                              minWidth: '220px',
                              maxWidth: 'calc(100% - 32px)',
                              width: 'max-content',
                              wordBreak: 'break-word',
                              textAlign: 'center',
                            }}
                            className="add-tooltip"
                          >
                            <div style={{ marginBottom: '8px' }}>{addTitle}</div>
                            <a
                              href={addLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#cd55e0', textDecoration: 'underline', wordBreak: 'break-all' }}
                            >
                              @{addLink}
                            </a>
                          </div>
                        )}
                      </span>
                    </div>
                  )}

                  <Row style={{ width: '100%' }}>
                    <Chart
                      power={{
                        currentKw: !onlineStatus ? 0 : Number(lastJob?.powerWt) / 1000,
                        maxKw: station?.maxPowerWt > 0 ? station.maxPowerWt / 1000 : CHART_MAX_KW,
                      }}
                      voltage={!onlineStatus ? 0 : Number(Math.round(lastJob?.voltage))}
                      soc={socForUi}
                      offline={onlineStatus === false}
                      onChartClick={openPowerHistoryChart}
                    />
                  </Row>
                  <Row id="fullInfoKwtMetricsBox" className="justify-content-center">
                    <Col id="fullInfoKwtChargedBox" className="text-center mb-2">
                      <PowerMetricsColor id="fullInfoKwtChargedTitle" className="mb-0 textTitle ">
                        {t('charging')}
                      </PowerMetricsColor>
                      <p id="fullInfoKwtChargedInfo" className="textInfo voltTitle text">
                        {chargeStatus} {t('wt')}
                      </p>
                    </Col>
                    {isEcSession && sessionLeftKwh != null ? (
                      <Col id="fullInfoKwtPowerBox" xs lg={6} className="text-center">
                        <PowerMetricsColor id="fullInfoKwtPowerTitle" className="mb-0 textTitle ">
                          {t('energyCard.leftKwh')}
                        </PowerMetricsColor>
                        <p id="fullInfoKwtPowerInfo" className="textInfo voltTitle text">
                          {sessionLeftKwh.toFixed(1)} {t('wt')}
                        </p>
                      </Col>
                    ) : (
                      lastJob?.periodKwt !== 220 && (
                        <Col id="fullInfoKwtPowerBox" xs lg={6} className="text-center">
                          <PowerMetricsColor id="fullInfoKwtPowerTitle" className="mb-0 textTitle ">
                            {t('payedForKwh')}
                          </PowerMetricsColor>
                          <p id="fullInfoKwtPowerInfo" className="textInfo voltTitle text">
                            {periodKwh}
                            {t('btns.startKwt')}
                          </p>
                        </Col>
                      )
                    )}
                    {kwhChargeProgress && (
                      <div
                        className="kwhChargeProgress"
                        role="progressbar"
                        aria-valuenow={Math.round(kwhChargeProgress.pct)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                      >
                        <div className="kwhChargeProgressTrack">
                          <div className="kwhChargeProgressFill" style={{ width: `${kwhChargeProgress.pct}%` }} />
                        </div>
                      </div>
                    )}
                  </Row>
                </Card>

                <EmsBatteryEcoCard
                  socPercent={emsSocPercent}
                  batteryCapacityKwh={emsBatteryCapacityKwh}
                  batteryPowerW={emsData.batteryPowerW}
                  iconRef={greenEnergyBatteryIconRef}
                  socPriceTiers={socPriceTiers}
                  openEmsInverterUrl={openEmsInverterUrl}
                />
              </div>
              <p className="ems-station-source-hint">{t('stationAdditionalSourceBess')}</p>
            </div>
          )}

          {!hasEms && (
            <Card
              variant="invert"
              className="charging-metrics-card"
              style={{ width: '100%', justifyContent: 'center', alignItems: 'center', marginBlock: '10px' }}
            >
              <Col id="onlineStationKmChargedBox" xs lg={6} className="text-center m-3">
                <PowerMetricsColor id="onlineStationKmChargedTitle" className="mb-0 textTitle">
                  {t('power')}
                </PowerMetricsColor>
                <p
                  id="onlineStationKmChargedInfo"
                  className={`textInfo text${onlineStatus === false ? ' charging-power-value--offline' : ''}`}
                  style={{ fontSize: 'calc(1.5rem + 2.2vw)' }}
                >
                  {onlineStatus === false ? (
                    <>{t('onlineStatus')}</>
                  ) : (
                    <>
                      {kWtPower.toFixed(2)} {t('powerWt')}
                    </>
                  )}
                </p>
              </Col>

              {hasAddTitle && addTitle && addLink && (
                <div
                  ref={addTooltipRef}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    margin: '12px 0',
                  }}
                >
                  <span style={{ position: 'relative', display: 'inline-block' }}>
                    <InfoOutlinedIcon
                      style={{ color: '#C83DC5', cursor: 'pointer', fontSize: 26, display: 'block', margin: '0 auto' }}
                      onMouseOver={() => setAddTooltipOpen(true)}
                      onMouseOut={() => setAddTooltipOpen(false)}
                    />
                    {addTooltipOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          left: '50%',
                          top: '100%',
                          transform: 'translateX(-50%)',
                          marginTop: '8px',
                          background: '#111',
                          color: '#fff',
                          border: '1px solid #C83DC5',
                          borderRadius: '8px',
                          padding: '14px 20px',
                          whiteSpace: 'normal',
                          zIndex: 1000,
                          minWidth: '220px',
                          maxWidth: 'calc(100% - 32px)',
                          width: 'max-content',
                          wordBreak: 'break-word',
                          textAlign: 'center',
                        }}
                        className="add-tooltip"
                      >
                        <div style={{ marginBottom: '8px' }}>{addTitle}</div>
                        <a
                          href={addLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#cd55e0', textDecoration: 'underline', wordBreak: 'break-all' }}
                        >
                          @{addLink}
                        </a>
                      </div>
                    )}
                  </span>
                </div>
              )}

              <Row style={{ width: '100%' }}>
                <Chart
                  power={{
                    currentKw: !onlineStatus ? 0 : Number(lastJob?.powerWt) / 1000,
                    maxKw: station?.maxPowerWt > 0 ? station.maxPowerWt / 1000 : CHART_MAX_KW,
                  }}
                  voltage={!onlineStatus ? 0 : Number(Math.round(lastJob?.voltage))}
                  soc={socForUi}
                  offline={onlineStatus === false}
                  onChartClick={openPowerHistoryChart}
                />
              </Row>
              <Row id="fullInfoKwtMetricsBox" className="justify-content-center">
                <Col id="fullInfoKwtChargedBox" className="text-center mb-2">
                  <PowerMetricsColor id="fullInfoKwtChargedTitle" className="mb-0 textTitle ">
                    {t('charging')}
                  </PowerMetricsColor>
                  <p id="fullInfoKwtChargedInfo" className="textInfo voltTitle text">
                    {chargeStatus} {t('wt')}
                  </p>
                </Col>
                {isEcSession && sessionLeftKwh != null ? (
                  <Col id="fullInfoKwtPowerBox" xs lg={6} className="text-center">
                    <PowerMetricsColor id="fullInfoKwtPowerTitle" className="mb-0 textTitle ">
                      {t('energyCard.leftKwh')}
                    </PowerMetricsColor>
                    <p id="fullInfoKwtPowerInfo" className="textInfo voltTitle text">
                      {sessionLeftKwh.toFixed(1)} {t('wt')}
                    </p>
                  </Col>
                ) : (
                  lastJob?.periodKwt !== 220 && (
                    <Col id="fullInfoKwtPowerBox" xs lg={6} className="text-center">
                      <PowerMetricsColor id="fullInfoKwtPowerTitle" className="mb-0 textTitle ">
                        {t('payedForKwh')}
                      </PowerMetricsColor>
                      <p id="fullInfoKwtPowerInfo" className="textInfo voltTitle text">
                        {periodKwh}
                        {t('btns.startKwt')}
                      </p>
                    </Col>
                  )
                )}
                {kwhChargeProgress && (
                  <div
                    className="kwhChargeProgress"
                    role="progressbar"
                    aria-valuenow={Math.round(kwhChargeProgress.pct)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  >
                    <div className="kwhChargeProgressTrack">
                      <div className="kwhChargeProgressFill" style={{ width: `${kwhChargeProgress.pct}%` }} />
                    </div>
                  </div>
                )}
              </Row>
            </Card>
          )}
        </Box>
      </>
    );
  }

  function showHumanInfo() {
    // Energy-card (kWh) sessions: hide UAH spent/paid — billing is in kWh.
    const rate = costPerKwt / 100;
    const paidKwh = lastJob?.periodKwt && lastJob.periodKwt !== 220 ? lastJob.periodKwt : kWhCharged;
    const spentUah = (kWhCharged * rate).toFixed(2);
    const paidUah = (paidKwh * rate).toFixed(2);
    const showPaid = spentUah !== paidUah;
    return (
      <>
        {!isEcSession && (
          <Row
            id="onlineStationKmMetricsBox"
            className="justify-content-center infoContainer"
            style={{ marginTop: '32px' }}
          >
            <Col id="onlineStationKmChargedBox" xs={showPaid ? 6 : 12} lg={showPaid ? 6 : 12} className="text-center">
              <PowerMetricsColor id="onlineStationKmChargedTitle" className="mb-0 textTitle">
                {t('spent')}
              </PowerMetricsColor>
              <p id="onlineStationKmChargedInfo" className="textInfo text">
                {spentUah} ₴
              </p>
            </Col>
            {showPaid && (
              <Col id="onlineStationKmPowerBox" xs={6} lg={6} className="text-center">
                <PowerMetricsColor id="onlineStationKmPowerTitle" className="mb-0 textTitle">
                  {t('payedForKwh')}
                </PowerMetricsColor>
                <p id="onlineStationKmPowerInfo" className="textInfo text">
                  {paidUah} ₴
                </p>
              </Col>
            )}
          </Row>
        )}
        {!isEcSession && lastJob?.periodKwt !== 220 && lastJob?.periodKwt >= 4 && (
          <ChargingProgress
            status={lastJob}
            online={onlineStatus}
            widthContainer={'260px'}
            marginContainer={'12px auto 8px'}
            widthDot={'52px'}
            heightDot={'52px'}
            periodKwt={lastJob?.periodKwt}
            carKwtKmRatio={carKwtKmRatio}
            showKmLabel={hasActualKmRatio}
          />
        )}

        {timer !== null ? (
          <>
            {socForUi !== 0 && socForUi !== undefined && socForUi !== null && (
              <Row className="justify-content-center text-center">
                <Col xs={12}>
                  <span style={{ fontSize: '32px', fontStyle: 'italic', color: socUiColors.color }}>
                    {socForUi}
                    <span style={{ fontFamily: FONT_STACK_PERCENT_SAFE }}>{' %'}</span>
                  </span>
                </Col>
              </Row>
            )}
          </>
        ) : (
          <LoadingSm />
        )}
      </>
    );
  }

  function getDynamicTariffTooltipText() {
    return getDynamicTariffScheduleFromStation(station, 'ua');
  }

  // Add tooltip data from station name
  function getAddTooltipData() {
    if (!station?.name) return null;

    const addTitleMatch = station.name.match(/addTitle:"([^"]+)"/i);
    const addLinkMatch = station.name.match(/addLink:"([^"]+)"/i);

    if (!addTitleMatch || !addLinkMatch) return null;

    return {
      title: addTitleMatch[1],
      link: addLinkMatch[1],
    };
  }

  return (
    <div id="onlineStationBox">
      {/* Dynamic Tariff Tooltip */}
      {getDynamicTariffTooltipText() && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
          <span style={{ position: 'relative', display: 'inline-block' }}>
            <InfoOutlinedIcon
              style={{ color: '#C83DC5', cursor: 'pointer', fontSize: 26 }}
              onMouseOver={() => setTariffTooltipOpen(true)}
              onMouseOut={() => setTariffTooltipOpen(false)}
            />
            {tariffTooltipOpen && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '100%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  background: '#111',
                  color: '#fff',
                  border: '1px solid #C83DC5',
                  borderRadius: '8px',
                  padding: '14px 20px',
                  whiteSpace: 'pre-line',
                  textAlign: 'left',
                  lineHeight: 1.45,
                  zIndex: 1000,
                  minWidth: '260px',
                  maxWidth: 'min(400px, 92vw)',
                }}
                className="dynamic-tariff-tooltip"
              >
                {getDynamicTariffTooltipText()}
              </div>
            )}
          </span>
        </div>
      )}
      {showHumanInfo()}
      {lastJob?.showStop && onlineStatus && (
        <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 8px 16px' }}>
          <Button
            onClick={hideSpinner ? () => setStopConfirmOpen(true) : undefined}
            disabled={!hideSpinner}
            aria-busy={!hideSpinner}
            className={`mainBtn buttonForOfflineStation w-100 ${getActionButtonLoadingClassName(!hideSpinner)}`}
            style={{ minHeight: '56px' }}
          >
            {!hideSpinner ? <ActionButtonSpinner /> : <span className="mainBtnText">{t('stopButton')}</span>}
          </Button>
        </div>
      )}
      {showPowerInfo()}
      {!onlineStatus &&
        (isStationInService(station) ? (
          <p className="port-in-service-message charging-port-inservice-message">
            {getPortInServiceMessage(station, t)}
          </p>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button onClick={callToSupport} className="mainBtn buttonForOfflineStation w-100">
              <span className="mainBtnText">{t('contactUs')}</span>
            </Button>
          </div>
        ))}

      {greenEnergySolarFlowPx &&
        Array.from({ length: GREEN_ENERGY_FLOW_BUBBLE_COUNT }, (_, i) => (
          <span
            key={`gf-flow-solar-${i}`}
            className="green-energy-flow-bubble-fixed green-energy-flow-bubble-fixed--solar"
            aria-hidden
            style={greenEnergyFlowBubbleStyle(greenEnergySolarFlowPx, i)}
          />
        ))}
      {greenEnergyBatteryFlowPx &&
        Array.from({ length: GREEN_ENERGY_FLOW_BUBBLE_COUNT }, (_, i) => (
          <span
            key={`gf-flow-bat-${i}`}
            className="green-energy-flow-bubble-fixed"
            aria-hidden
            style={greenEnergyFlowBubbleStyle(greenEnergyBatteryFlowPx, i, { invertDx: true })}
          />
        ))}

      <ChargingPowerHistoryModal
        open={powerHistoryOpen}
        onClose={() => setPowerHistoryOpen(false)}
        stationNumber={station?.number}
        lastJob={lastJob}
        plugType={station?.plugType}
      />

      <StopChargingConfirmModal
        modalOpen={stopConfirmOpen}
        setModalOpen={setStopConfirmOpen}
        onConfirm={stopProcessCharging}
        isLoading={!hideSpinner}
      />

      <ModalContacts modalOpen={isOpen} setModalOpen={setIsOpen} />
    </div>
  );
}
